<template>
  <span>
    <v-tooltip v-if="tooltip" bottom>
      <template v-slot:activator="{ on, attrs }">
        <v-btn v-bind="mergedBtnProps(attrs)" v-on="on" :disabled="!hasSource" @click.stop="handleClick">
          <v-icon v-if="icon" small>
            {{ icon }}
          </v-icon>

          <span v-if="label" class="ml-1">
            {{ label }}
          </span>
        </v-btn>
      </template>

      <span>{{ tooltip }}</span>
    </v-tooltip>

    <v-btn v-else v-bind="mergedBtnProps()" :disabled="!hasSource" @click.stop="handleClick">
      <v-icon v-if="icon" small>
        {{ icon }}
      </v-icon>

      <span v-if="label" class="ml-1">
        {{ label }}
      </span>
    </v-btn>

    <v-dialog v-model="dlg" max-width="900">
      <v-card>
        <v-card-title class="py-2">
          <span class="text-subtitle-1">
            {{ fileName }}
          </span>

          <v-spacer />

          <v-btn icon :disabled="!hasSource" @click="download">
            <v-icon>mdi-download</v-icon>
          </v-btn>

          <v-btn icon @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <v-card-text class="pa-0">
          <div v-if="isImage" class="pa-2">
            <v-img :src="preview_url" contain max-height="700" />
          </div>

          <iframe v-else-if="isPdf" :src="preview_url" width="100%" height="700" frameborder="0" />

          <pre v-else-if="isText" class="pa-4 ma-0 document-text">{{ text_content }}</pre>
        </v-card-text>
      </v-card>
    </v-dialog>
  </span>
</template>

<script>
export default {
  name: "BtnFilePreview",

  props: {
    doc: {
      type: Object,
      default: null,
    },

    tooltip: {
      type: String,
      default: "Ver documento",
    },

    icon: {
      type: String,
      default: "mdi-file-eye",
    },

    label: {
      type: String,
      default: "",
    },

    btnProps: {
      type: Object,
      default: () => ({}),
    },

    forceNewTab: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      dlg: false,
      preview_url: null,
      text_content: "",
    };
  },

  computed: {
    base64() {
      if (!this.doc) {
        return "";
      }

      return this.doc.base64 || this.doc.cnt || "";
    },

    extension() {
      if (!this.doc || !this.doc.ext) {
        return "";
      }

      return String(this.doc.ext)
        .trim()
        .toLowerCase();
    },

    mime() {
      if (this.doc && this.doc.mime) {
        return this.doc.mime;
      }

      return this.getMimeByExtension(this.extension);
    },

    fileName() {
      if (!this.doc || !this.doc.name) {
        return "Documento";
      }

      return this.doc.name;
    },

    hasSource() {
      return Boolean(this.base64);
    },

    isImage() {
      return (
        this.mime.indexOf("image/") === 0 ||
        [
          "png",
          "jpg",
          "jpeg",
          "webp",
          "gif",
        ].includes(this.extension)
      );
    },

    isPdf() {
      return (
        this.mime === "application/pdf" ||
        this.extension === "pdf"
      );
    },

    isText() {
      return [
        "xml",
        "txt",
      ].includes(this.extension);
    },

    canPreview() {
      return (
        this.isImage ||
        this.isPdf ||
        this.isText
      );
    },
  },

  beforeDestroy() {
    this.revokePreviewUrl();
  },

  methods: {
    mergedBtnProps(attrs) {
      return Object.assign(
        {
          "x-small": true,
          icon: !this.label,
          color: "primary",
        },
        attrs || {},
        this.btnProps || {},
      );
    },

    getMimeByExtension(extension) {
      const types = {
        pdf: "application/pdf",
        png: "image/png",
        jpg: "image/jpeg",
        jpeg: "image/jpeg",
        webp: "image/webp",
        gif: "image/gif",
        xml: "application/xml",
        txt: "text/plain",
        csv: "text/csv",
        xls: "application/vnd.ms-excel",
        xlsx:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      };

      return types[extension] ||
        "application/octet-stream";
    },

    handleClick() {
      if (!this.hasSource) {
        return;
      }

      if (this.canPreview) {
        this.openPreviewDialog();

        return;
      }

      this.download();
    },

    openPreviewDialog() {
      this.revokePreviewUrl();

      this.text_content = "";

      if (this.isText) {
        this.text_content = this.getTextFromBase64(
          this.base64
        );

        this.dlg = true;

        return;
      }

      const url = this.buildBlobUrl();

      if (!url) {
        return;
      }

      this.preview_url = url;
      this.dlg = true;
    },

    closeDialog() {
      this.dlg = false;
      this.text_content = "";

      this.revokePreviewUrl();
    },

    download() {
      const url = this.buildBlobUrl();

      if (!url) {
        return;
      }

      const link = document.createElement("a");

      link.href = url;
      link.download = this.fileName;

      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);

      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 30000);
    },

    buildBlobUrl() {
      const blob = this.getBlobFromBase64(
        this.base64,
        this.getMimeByExtension(this.extension)
      );

      return blob
        ? URL.createObjectURL(blob)
        : null;
    },

    revokePreviewUrl() {
      if (!this.preview_url) {
        return;
      }

      URL.revokeObjectURL(this.preview_url);

      this.preview_url = null;
    },

    getBase64Content(base64) {
      let content = String(base64 || "").trim();

      const comma_index = content.indexOf(",");

      if (
        content.indexOf("data:") === 0 &&
        comma_index !== -1
      ) {
        content = content.slice(comma_index + 1);
      }

      while (content.length % 4 !== 0) {
        content += "=";
      }

      return content;
    },

    getBlobFromBase64(base64, mime) {
      if (!base64) {
        return null;
      }

      try {
        const content = this.getBase64Content(base64);
        const binary = atob(content);
        const bytes = new Uint8Array(binary.length);

        for (
          let i = 0;
          i < binary.length;
          i++
        ) {
          bytes[i] = binary.charCodeAt(i);
        }

        return new Blob(
          [bytes],
          {
            type:
              mime ||
              "application/octet-stream",
          }
        );
      } catch (error) {
        console.log(error);

        return null;
      }
    },

    getTextFromBase64(base64) {
      if (!base64) {
        return "";
      }

      try {
        const content = this.getBase64Content(base64);
        const binary = atob(content);
        const bytes = new Uint8Array(binary.length);

        for (
          let i = 0;
          i < binary.length;
          i++
        ) {
          bytes[i] = binary.charCodeAt(i);
        }

        return new TextDecoder("utf-8").decode(bytes);
      } catch (error) {
        console.log(error);

        return "";
      }
    },
  },
};
</script>

<style scoped>
.document-text {
  max-height: 700px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: monospace;
}
</style>